<template>
  <div class="form">
    <FormDate
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :permission="permission"
      :roomKey="roomKey"
      :rid="rid"
      :guestUserInfo="guestUserInfo"
      :isStaff="isStaff"
      :isPublic="isPublic"
      :reserveDate="reserve.date"
      :cancelDate="cancelDate"
      :userLimitedInfo="userLimitedInfo"
      @setDate="setDate" />
    <FormTime
      v-if="validate.date"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :isStaff="isStaff"
      :isPublic="isPublic"
      :guestUserInfo="guestUserInfo"
      :reserveDate="reserve.date"
      :reserveTime="reserve.time"
      :timeInfo="info.time"
      @setTime="setTime" />
    <FormSection
      v-if="validate.date && validate.time"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :isPublic="isPublic"
      :sectionInfo="info.section"
      @setSection="setSection" />
    <FormPlace
      v-if="validate.section && place && place.selectable"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :isPublic="isPublic"
      :placeInfo="place.list"
      :isSelectable="place.selectable"
      :isStaff="isStaff"
      @setPlace="setPlace" />
    <FormPerson
      v-if="validate.place"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :isPublic="isPublic"
      :reservePerson="reserve.person"
      :userLimitedInfo="userLimitedInfo"
      :min="place.active.min"
      :max="personMax"
      @setPerson="setPerson" />
    <FormSeat
      v-if="seatMap && validate.person"
      :lang="lang"
      :ln="ln"
      :locale="locale"
      :info="info"
      :roomKey="roomKey"
      :rid="rid"
      :isPublic="isPublic"
      :placeId="select.place"
      :seatMap="seatMap"
      :seatCount="seatCount"
      :remainSeat="remainSeat"
      @setSeatMap="setSeats" />
    <p v-if="checkDateMessage" class="noData toCapitalize notranslate">
      {{checkDateMessage}}
    </p>
    <p v-else-if="!validate.date" class="noData toCapitalize notranslate">
      {{locale.message_select_date}}
    </p>
    <p v-else-if="!validate.time" class="noData toCapitalize notranslate">
      {{locale.message_select_time}}
    </p>
    <p v-else-if="validate.place && !validate.person"
      class="noData toCapitalize notranslate">
      {{ locale.message_select_person }}
    </p>
    <p v-else-if="validate.section && place && !place.selectable"
      class="noData toCapitalize notranslate">
      {{ locale.nodata_reservation_section }}
    </p>
    <div
      v-if="validate.seat && checkComplete">
      <FormUserInfo
        :lang="lang"
        :ln="ln"
        :locale="locale"
        :info="info"
        :roomKey="roomKey"
        :rid="rid"
        :guestUserInfo="guestUserInfo"
        :isPublic="isPublic"
        @setUserInfo="setUserInfo" />
      <FormAdditionalInfo
        v-if="info.additionalInfo && info.additionalInfo.length > 0"
        :lang="lang"
        :locale="locale"
        :additionalInfo="info.additionalInfo"
        :value="reserve.additionalInfo"
        @setAdditionalInfo="reserve.additionalInfo = $event" />
    </div>
    <div v-if="info.infoTxt[lang].length">
      <pre v-html="info.infoTxt[lang]" class="messageBox toCapitalize"></pre>
    </div>
    <div class="formGroup btns mt30 notranslate">
      <button
        :disabled="!validate.result"
        type="button"
        class="btn btnRound btnFull toCapitalize"
        @click="confirmSummary">
        {{locale.reservation_confirm}}
      </button>
    </div>
    <!-- 예약내용 레이어 -->
    <Summary
      v-if="summary"
      :locale="locale"
      :lang="lang"
      :summary="summary"
      @close="summary = null"
      @confirm="submit" />
    <!-- //예약내용 레이어 -->
  </div>
</template>

<script>
import moment from 'moment/moment'
import regExp from '@/common/regExp'
import Summary from '@/components/Reservation/Summary'
import SeatMap from '@/components/Reservation/SeatMap'
import FormDate from '@/components/Reservation/FormDate'
import FormTime from '@/components/Reservation/FormTime'
import FormPerson from '@/components/Reservation/FormPerson'
import FormSection from '@/components/Reservation/FormSection'
import FormPlace from '@/components/Reservation/FormPlace'
import FormSeat from '@/components/Reservation/FormSeat'
import FormUserInfo from '@/components/Reservation/FormUserInfo'
import FormAdditionalInfo from './FormAdditionalInfo.vue'

export default {
  name: 'ReservationForm',
  props: [
    'lang',
    'ln',
    'locale',
    'info',
    'roomKey',
    'rid',
    'isPublic'
  ],
  components: {
    Summary,
    SeatMap,
    FormDate,
    FormTime,
    FormPerson,
    FormSection,
    FormPlace,
    FormSeat,
    FormUserInfo,
    FormAdditionalInfo
  },
  computed: {
    isStaff () {
      const staff = this.$store.state.authentication.staff
      return staff
    },
    guestUserInfo () {
      return this.$store.getters['guest/getGuestUserInfo'] // rid guest info
    },
    dateFormat () {
      return this.$store.getters['common/getDateFormat']
    },
    readyInfo () {
      return this.reserve.date && this.reserve.time && this.reserve.person
    },
    section () { // 선택 가능한 section, 선택된 section
      const sectionId = this.select.section
      const list = (sectionId && this.info && this.info.section) || []
      const active = (list && list.find(x => x.id === sectionId)) || null
      return {
        list: list,
        active: active
      }
    },
    place () { // 선택 가능한 place, 선택된 place
      const isStaff = this.isStaff
      const sectionId = this.section && this.section.active && this.section.active.id
      const placeId = this.select.place
      const section = sectionId &&
        this.info &&
        this.info.section &&
        this.info.section.find(x => x.id === sectionId)

      const list = (section && section.place) || []
      const active = (list && list.find(x => x.placeId === placeId)) || null
      const remainSeat = this.remainSeat

      const placeList = list.map(x => {
        const placeRemainSeat = remainSeat && remainSeat[x.placeId]

        const isDisabled = isStaff // staff는 잔여 자리 관계 없이 설정 가능
          ? false
          : !placeRemainSeat ||
            !placeRemainSeat.remainSeats ||
            placeRemainSeat.remainSeats.length <= 0 // 해당 place에 1자리 이상 남았는지?

        return {
          ...x,
          current: (placeRemainSeat && placeRemainSeat.current) || 0, // 조회된 예약수
          disabled: x.disabled || isDisabled
        }
      })

      return {
        list: placeList,
        selectable: placeList.filter(x => !x.disabled).length > 0,
        active: active
      }
    },
    personMax () {
      const place = this.place && this.place.active

      if (!place) return 0
      if (!this.info.multiple) return place.max

      const remain = this.remainSeat &&
        this.remainSeat[place.placeId] &&
        this.remainSeat[place.placeId].remainSeats

      return Math.min(
        place.max,
        remain ? remain.length : 0
      )
    },
    seat () {
      const place = this.place && this.place.active
      const list = (place && place.seat) || []
      const remainSeat = this.remainSeat
      return {
        list: list && list.map(x => {
          const remainSeats = remainSeat[x.placeId] && remainSeat[x.placeId].remainSeats
          return {
            ...x,
            disabled: remainSeats && !remainSeats.includes(x.seatId)
          }
        })
      }
    },
    seatMap () { // 자리 직접선택에 필요한 정보 맵핑
      const section = this.section && this.section.active
      return section && !section.autoAssign
        ? {
          seats: section.seatMap,
          seatObj: section.seatObj,
          info: {
            showPlaceName: section.showPlaceName,
            showSeatName: section.showSeatName,
            useSeatMap: section.useSeatMap
          }
        }
        : null
    },
    seatCount () { // info.multiple ? 인원수대로 자리 지정(수영장 입장) : 한자리(레스토랑 테이블)
      const person = parseInt(this.reserve.person)
      return this.info.multiple ? person : 1
    },
    validate () {
      const isStaff = this.isStaff
      const isDate = this.isVal(this.reserve.date)
      const isTime = this.reserve.time && this.isVal(this.reserve.time.id)
      const isPerson = this.isVal(this.reserve.person)
      const isSection = this.section && this.section.active && this.isVal(this.section.active.id)
      const isPlace = this.place && this.place.active && this.isVal(this.place.active)
      const isSeat = this.reserve.seats && this.reserve.seats.length > 0
      const isFirstName = this.isVal(this.reserve.firstName)
      const isLastName = this.isVal(this.reserve.lastName)
      const isPhone = this.info.bookerTel
        ? this.validatePhone && this.isVal(this.reserve.phone)
        : true
      const isEmail = this.info.bookerEmail
        ? this.isVal(this.reserve.email)
        : true
      const isPhoneOrEmail = this.info.bookerTelOrEmail
        ? (this.validatePhone && this.isVal(this.reserve.phone) || this.isVal(this.reserve.email))
        : true

      const isUserInfo =
        isFirstName &&
        isLastName &&
        isPhone &&
        isEmail &&
        isPhoneOrEmail

      const additionalInfo = this.info.additionalInfo || []
      const additionalValues = this.reserve.additionalInfo || []

      const isAdditionalInfo = additionalInfo.every(item => {
        if (!item.isRequired) return true

        const target = additionalValues.find(x => x.id === item.id)

        if (!target) return false

        if (item.type === 'input') {
          return this.isVal(target.value)
        }

        return Array.isArray(target.value) && target.value.length > 0
      })

      return {
        isStaff,
        date: isDate,
        time: isTime,
        person: isPerson,
        section: isSection,
        place: isPlace,
        seat: isSeat,
        firstName: isFirstName,
        lastName: isLastName,
        phone: isPhone,
        email: isEmail,
        additionalInfo: isAdditionalInfo,
        result:
          isDate &&
          isTime &&
          isSection &&
          isPlace &&
          isSeat &&
          isUserInfo &&
          isPerson &&
          isAdditionalInfo
      }
    }
  },
  watch: {
    place: {
      deep: true,
      handler (data) {
        if (!data.selectable) {
          this.select.place = null
          this.reserve.seats = null
          this.checkComplete = false
        }
      }
    }
  },
  created () {
    if (this.info.disabled) {
      this.checkDateMessage = this.locale.message_reservation_disabled
    } else if (!this.isStaff && this.info.availableType) {
      if (this.info.availableType === 'ota' && !this.roomKey) { // 기본값 투숙객(ota) 검사
        this.checkDateMessage = this.locale.alert_reservation_permission
      } else if (this.info.availableType === 'rid' && (!this.rid && !this.roomKey)) { // 예약자까지 사용 가능하면 예약+투숙 모두 포함하여 ota 또는 rid 검사
        this.checkDateMessage = this.locale.alert_reservation_permission
      } else if (this.info.availableType === 'package') {
        if (!this.rid && !this.roomkey) {
          this.checkDateMessage = this.locale.alert_reservation_permission
        } else {
          this.getPackageServiceInfo()
        }
      } else {
        this.permission = true
      }
    } else {
      this.permission = true
    }
  },
  data () {
    let userInfo = {
      ...this.info.bookerEmail && {
        email: null
      },
      ...this.info.bookerTel && {
        phone: null
      }
    }
    let validatePhone = this.info.bookerTel ? false : true
    return {
      permission: false,
      select: {
        section: null,
        place: null
      },
      reserve: {
        date: null,
        time: null,
        person: null,
        seats: null,
        firstName: null,
        lastName: null,
        ...userInfo,
        additionalInfo: []
      },
      validatePhone,
      remainSeat: null,
      checkDateMessage: null,
      checkComplete: false,
      summary: null,
      cancelDate: null,
      userLimitedInfo: null
    }
  },
  methods: {
    isVal (str) {
      return regExp.replaceAllEmpty(str || '').length > 0
    },
    async getPackageServiceInfo () { // 패키지 고객인 경우
      const res = await this.$store.dispatch('reservation/getPackageReservationInfo', {
        linkUUID: this.info.id
      })
      if (res && res.availableDate) {
        if (res.limit && res.limit > 0) {
          this.userLimitedInfo = res
          this.permission = true
        } else {
          this.checkDateMessage = this.locale.alert_reservation_already_limit
        }
      } else {
        this.checkDateMessage = this.locale.alert_reservation_permission
      }
    },
    getResultData (code) {
      return this.$store.getters['reservation/getReservationResultData'](code)
    },
    setDateFormat (date) {
      return moment(date).format(this.dateFormat.toUpperCase())
    },
    async setDate (date) { // 날짜별 예약가능여부 체크
      this.$store.commit('loading', true)
      const checkDate = await this.$store.dispatch('reservation/checkReservationDate', {
        property: this.info.id,
        date: date
      })
      if (checkDate && checkDate.code) {
        const code = this.getResultData(checkDate.code)
        if (checkDate.code === 200) {
          this.checkDateMessage = null
          this.reserve.date = date
        } else {
          this.checkDateMessage = this.locale[`alert_reservation_${code === 'already_self' ? 'already_self_date' : code}`]
          this.cancelDate = date
          this.reserve.date = null
          this.setSection()
        }
      } else {
        this.checkDateMessage = null
        this.cancelDate = date
        this.reserve.date = null
        this.setSection()
        alert(this.locale.alert_data_load_fail)
      }
      this.reserve.time = null
      this.reserve.person = null
      // this.reserve.person = this.userLimitedInfo ? this.userLimitedInfo.person : null
      this.setSection()
      this.$store.commit('loading', false)
      setTimeout(() => {
        this.cancelDate = null
      }, 500)
    },
    setTime (time) {
      this.reserve.time = time

      this.select.section = null
      this.select.place = null

      this.reserve.person = null
      this.reserve.seats = null

      this.remainSeat = null
      this.checkComplete = false
    },
    async setPerson (person) {
      this.reserve.person = parseInt(person)
      this.reserve.seats = null
      this.checkComplete = false

      if (this.select.section) {
        await this.setSection(this.select.section)

        if (
          this.select.place &&
          this.section.active &&
          this.section.active.autoAssign
        ) {
          this.seatSeatAssign()
        }
      }
    },
    async setSection (section) {
      if (section) {
        const isSectionChanged = this.select.section !== section

        this.$store.commit('loading', true)

        const checkSection = await this.$store.dispatch(
          'reservation/checkReservationSection',
          {
            property: this.info.id,
            date: this.reserve.date,
            time: this.reserve.time.id,
            section: section
          }
        )

        if (checkSection) {
          if (isSectionChanged) {
            this.select.place = null
            this.reserve.person = null
            this.reserve.seats = null
            this.checkComplete = false
          }

          this.select.section = section
          this.remainSeat = checkSection
        } else {
          this.select.section = null
          this.select.place = null
          this.reserve.person = null
          this.reserve.seats = null
          this.remainSeat = null
          this.checkComplete = false

          alert(this.locale.alert_data_load_fail)
        }

        this.$store.commit('loading', false)
      } else {
        this.select.section = null
        this.select.place = null
        this.reserve.person = null
        this.reserve.seats = null
        this.remainSeat = null
        this.checkComplete = false
      }
    },
    setPlace (place) {
      if (place) {
        if (this.select.place === place) return

        this.select.place = place
        this.reserve.person = null
        this.reserve.seats = null
        this.checkComplete = false
      } else {
        this.select.place = null
        this.reserve.person = null
        this.reserve.seats = null
        this.checkComplete = false
      }
    },
    seatSeatAssign () {
      const isStaff = this.isStaff
      const isMultiple = this.info.multiple
      const count = this.seatCount
      const seatInfo = this.seat && this.seat.list[0] // staff인 경우 인원수만큼 name: '-1'인 seat를 생성
      const seat = this.seat && this.seat.list && this.seat.list.filter(x => !x.disabled).filter((x, index) => index < count).map(x => {
        return {
          placeId: x.placeId,
          placeName: x.placeName,
          seatId: x.seatId,
          seatName: x.name
        }
      })
      const addedLength = (isStaff && count - seat.length) || 0
      const addedSeat = addedLength > 0 // 모자란 자리만큼 예약
        ? makeSeat(addedLength, seatInfo)
        : []
      function langObj (obj) {
        let result = {}
        const keys = Object.keys(obj)
        for (let i = 0; i < keys.length; i++) {
          result = { ...result, [keys[i]]: '-1' }
        }
        return result
      }
      function makeSeat (addedLength, seatInfo) {
        let seatList = []
        for (let i = 0; i < addedLength; i++) {
          seatList.push({
            placeId: seatInfo.placeId,
            placeName: seatInfo.placeName,
            seatId: '-1',
            seatName: langObj(seatInfo.name)
          })
        }
        return seatList
      }
      this.setSeats(seat.concat(addedSeat))
    },
    setSeats (data) {
      const sectionId = this.section && this.section.active && this.section.active.id
      const sectionName = sectionId && this.section.active.name

      this.reserve.seats = data
        ? data.map(x => {
            return {
              section: {
                id: sectionId,
                name: sectionName
              },
              place: {
                id: x.placeId,
                name: x.placeName
              },
              seat: {
                id: x.seatId,
                name: x.seatName
              }
            }
          })
        : null

      this.checkReservation()
    },
    async checkReservation () {
      const validate = this.validate
      if (validate && !validate.isStaff && validate.date && validate.time && validate.person && validate.section && validate.place && validate.seat) {
        const checkReservation = await this.$store.dispatch('reservation/checkReservation', {
          property: this.info.id,
          date: this.reserve.date,
          time: this.reserve.time.id,
          seats: this.reserve.seats,
        })
        if (checkReservation && checkReservation.code) {
          const code = this.getResultData(checkReservation.code)
          if (checkReservation.code === 200) {
            this.checkDateMessage = null
            this.checkComplete = true
          } else {
            this.checkDateMessage = this.locale[`alert_reservation_${code}`]
            this.checkComplete = false
          }
        } else {
          this.checkDateMessage = this.locale.alert_data_load_fail
          this.checkComplete = false
          alert(this.locale.alert_data_load_fail)
        }
      } else if (validate.isStaff) {
        this.checkDateMessage = null
        this.checkComplete = true
      }
    },
    setUserInfo (data) {
      const target = data.target
      const info = data.info
      this.validatePhone = (this.info.bookerTel || this.info.bookerTelOrEmail) && data.phoneIsValid
      this.$set(this.reserve, target, info)
    },
    confirmSummary () {
      const getTimeLabel = () => {
        const timeId = this.reserve.time && this.reserve.time.id
        const timeInfo = this.info.time && this.info.time.find(x => x.timeId === timeId)
        return timeInfo && timeInfo.label
      }
      const additionalInfo = (this.info.additionalInfo || [])
        .map(item => {
          return (this.reserve.additionalInfo || [])
            .find(value => value.id === item.id)
        })
        .filter(item => {
          if (!item) return false
          if (item.type === 'input') {
            return this.isVal(item.value)
          }
          return Array.isArray(item.value) && item.value.length > 0
        })
      this.summary = {
        ...this.reserve,
        time: {
          ...this.reserve.time,
          label: getTimeLabel()
        },
        spaceName: this.info.name,
        sectionName: this.section.active.name,
        placeName: this.place.active.name,
        autoAssign: this.section.active.autoAssign,
        ...!this.section.active.autoAssign && {
          showPlaceName: this.section.active.showPlaceName,
          showSeatName: this.section.active.showSeatName
        },
        additionalInfo,
      }
    },
    submit () {
      const lang = this.ln
      const data = this.summary
      const location = window.location
      const siteInfo = this.$store.state.authentication
      const rid = siteInfo.rid ? `&rid=${siteInfo.rid}` : ''
      const ota = siteInfo.space && siteInfo.k ? `&space=${siteInfo.space}&k=${siteInfo.k}` : ''
      const additionalInfo = (this.info.additionalInfo || [])
        .map(item => {
          return (this.reserve.additionalInfo || [])
            .find(value => value.id === item.id)
        })
        .filter(item => {
          if (!item) return false

          if (item.type === 'input') {
            return this.isVal(item.value)
          }

          return Array.isArray(item.value) && item.value.length > 0
        })
      const setReservationData = {
        returnUrl: `${location.origin}${location.pathname}?site=${siteInfo.site}${rid}${ota}&frame=false#/${lang}/reservation/check/`,
        payload: {
          userLanguage: lang,
          multiple: this.info.multiple,
          date: this.reserve.date,
          time: this.reserve.time,
          person: this.reserve.person,
          space: {
            id: this.info.id,
            name: this.info.name,
            space: this.info.space
          },
          seats: this.reserve.seats,
          sectionName: this.summary.sectionName,
          placeName: this.summary.placeName,
          firstName: this.reserve.firstName,
          lastName: this.reserve.lastName,
          ...this.reserve.phone && {
            phone: this.reserve.phone
          },
          ...this.reserve.email && {
            email: this.reserve.email
          },
          ...additionalInfo.length > 0 && {
            additionalInfo
          }
        }
      }
      this.$store.commit('loading', true)
      this.$store.dispatch('reservation/setReservation', setReservationData).then((res) => {
        this.summary = null
        if (res && res.status && res.status === 200) {
          const sendResult = res.data && res.data.message
          const reservationId = sendResult && sendResult.order_uuid
          if (reservationId) {
            this.$router.push({
              name: 'reservationComplete',
              params: {
                lang: lang, reservationId: reservationId
              },
              // 예약결과를 예약자 모바일 또는 이메일로 발송, 사이트별 지원여부 다름
              ...sendResult && sendResult.type && {
                query: {
                  type: sendResult.type // ATA, SMS, LMS, EMAIL
                }
              }
            })
          } else {
            alert(this.locale.alert_fail_reservation)
          }
        } else {
          if (res.response && res.response.data && res.response.data.message) {
            const errorCode = res.response.data.message.code || 500
            const errorMessage = this.getResultData(errorCode)
            alert(this.locale[`alert_reservation_${errorMessage}`])
          } else {
            alert(this.locale.alert_fail_reservation)
          }
        }
        this.$store.commit('loading', false)
        this.$emit('init')
      })
    }
  }
}
</script>
